import React, { useCallback, useMemo } from 'react';
import {
  RegisterFormTypes,
  RegisterPageProps,
  useActiveAuthProvider,
  useLink,
  useRegister,
  useRouterContext,
  useRouterType,
  useTranslate,
} from '@refinedev/core';
import { ThemedTitle } from '../../../themedLayout/title';
import {
  bodyStyles,
  buttonStyle,
  cardHeaderStyles,
  containerStyles,
  headStyles,
  layoutStyles,
  PageTitleStyles,
} from './styles';
import {
  Button,
  Card,
  CardProps,
  Col,
  Form,
  FormProps,
  FormRule,
  Input,
  Layout,
  LayoutProps,
  Row,
  theme,
  Typography,
} from 'antd';
import { ConfirmPasswordForms } from './confirmPasswordForms';
import { useSharedEmail } from './auth/hooks';
import {
  clearFieldErrorMessageOnChange,
  isUserIdAvailableApi,
  onlyLatinCharsDigitsSpecialSymbolsRule,
  trimFormFieldValue,
  websiteRules,
} from '../../../../utils/form.constants';
import { useSearchParams } from 'react-router-dom';
import { companyRegistrationNumberRules } from '../../../../pages/payment-request/addCompany.constants';
import { AutoComplete } from '../../../inputs/autocomplete';
import { DownOutlined } from '@ant-design/icons';

const { useToken } = theme;

type RegisterProps = RegisterPageProps<LayoutProps, CardProps, FormProps>;
type RegisterVariables = {
  email?: string;
  password?: string;
  fullName?: string;
  address?: string;
  confirm?: string;
};
export const RegisterPage: React.FC<RegisterProps> = ({
  wrapperProps,
  contentProps,
  renderContent,
  formProps,
  title,
}) => {
  const { token } = useToken();
  const [form] = Form.useForm<RegisterVariables & RegisterFormTypes>();
  const translate = useTranslate();
  const routerType = useRouterType();
  const { Link: LegacyLink } = useRouterContext();
  const Link = useLink();
  const [searchParams] = useSearchParams();
  const [email, fullName, address] = useMemo(
    () => [
      searchParams.get('email') ?? undefined,
      decodeURIComponent(searchParams.get('fullName') ?? ''),
      decodeURIComponent(searchParams.get('address') ?? ''),
    ],
    [searchParams],
  );

  const userIdAvailableValidator: FormRule = useCallback(
    () => ({
      validateTrigger: 'onSubmit',
      async validator(_: any, value?: string) {
        if (value && value.length >= 3 && value.length <= 30 && !/\s/.test(value)) {
          const validationResult = await isUserIdAvailableApi(value);
          if (validationResult) {
            return Promise.resolve(true);
          } else {
            return Promise.reject('A user with this SMART UID already exists');
          }
        } else {
          return Promise.resolve(true);
        }
      },
    }),
    [],
  );

  const userIdContainsSpaceValidator: FormRule = useCallback(
    () => ({
      validator(_: any, value: string) {
        if (/\s/.test(value)) {
          return Promise.reject(new Error('SMART UID cannot contains spaces'));
        } else {
          return Promise.resolve();
        }
      },
      validateTrigger: 'onChange',
    }),
    [],
  );

  const ActiveLink = routerType === 'legacy' ? LegacyLink : Link;

  const authProvider = useActiveAuthProvider();
  const { mutate: register, isLoading } = useRegister<RegisterFormTypes>({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });
  const { saveEmail: shareEmail } = useSharedEmail();

  const onFinish = (values: RegisterVariables) => {
    shareEmail(values.email ?? '');
    register(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const PageTitle =
    title === false ? null : (
      <div
        style={{
          ...PageTitleStyles,
        }}
      >
        {title ?? <ThemedTitle collapsed={false} />}
      </div>
    );

  const CardHeader = (
    <div
      style={{
        ...cardHeaderStyles,
      }}
    >
      <Typography style={{ fontSize: '16px' }}>Registration</Typography>
      <ActiveLink
        style={{
          color: token.colorPrimaryTextHover,
          fontSize: '14px',
          marginLeft: 'auto',
          paddingRight: '15px',
          fontWeight: '500',
        }}
        to='/login'
      >
        {translate('pages.login.buttons.SignIn', 'Sign in')}
      </ActiveLink>
    </div>
  );

  const CardContent = (
    <Card
      title={CardHeader}
      headStyle={headStyles}
      bodyStyle={bodyStyles}
      style={{
        ...containerStyles,
      }}
      {...(contentProps ?? {})}
    >
      <Form<RegisterFormTypes>
        layout='vertical'
        form={form}
        requiredMark={false}
        {...formProps}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onValuesChange={clearFieldErrorMessageOnChange(form)}
        initialValues={{ email, address, fullName }}
      >
        <Form.Item
          name='email'
          rules={[
            onlyLatinCharsDigitsSpecialSymbolsRule,
            { required: true, validateTrigger: 'onSubmit' },
            {
              type: 'email',
              message: translate('pages.register.errors.validEmail', 'Invalid email address'),
              validateTrigger: 'onSubmit',
            },
          ]}
          style={{ marginTop: '-20px' }}
        >
          <Input
            size='large'
            placeholder={translate('pages.register.fields.email', 'Email')}
            style={{ ...buttonStyle }}
          />
        </Form.Item>
        <ConfirmPasswordForms />
        <Form.Item
          name='fullName'
          rules={[
            onlyLatinCharsDigitsSpecialSymbolsRule,
            {
              required: true,
              message: 'Please enter your full name or company name',
              validateTrigger: 'onSubmit',
            },
            {
              min: 3,
              message: 'Minimum 3 characters',
              validateTrigger: 'onSubmit',
            },
            {
              max: 256,
              message: 'Maximum 256 characters',
              validateTrigger: 'onSubmit',
            },
          ]}
        >
          <Input placeholder='Full name/Company name' style={{ ...buttonStyle }} size='large' />
        </Form.Item>
        <Form.Item name='regNo' rules={companyRegistrationNumberRules}>
          <Input placeholder='Reg. No (TIN, VAT)' style={{ ...buttonStyle }} size='large' />
        </Form.Item>
        <Form.Item
          name='countryOfIncorporation'
          rules={[
            {
              required: true,
              message: 'Country of incorporation is required',
              validateTrigger: 'onSubmit',
            },
          ]}
        >
          <AutoComplete
            resource='api/v1/worldCheck/countries'
            mapItem={(item: { value: string; label: string }) => ({
              value: item.label,
              key: item.value,
              label: <Typography.Text>{item.label}</Typography.Text>,
              data: item,
            })}
            notFoundPlaceholder='Start by typing country name'
          >
            <Input
              // style={{ fontSize: 16 }} 
              placeholder='Country of incorporation'
              size='large'
              suffix={<DownOutlined />}
            />
          </AutoComplete>
        </Form.Item>
        <Form.Item
          name='directorName'
          rules={[
            onlyLatinCharsDigitsSpecialSymbolsRule,
            {
              required: true,
              message: "Director's full name is required",
              validateTrigger: 'onSubmit',
              whitespace: true,
            },
          ]}
        >
          <Input placeholder="Director's full name" style={{ ...buttonStyle }} size='large' />
        </Form.Item>
        <Form.Item
          name='address'
          rules={[
            onlyLatinCharsDigitsSpecialSymbolsRule,
            { required: true, validateTrigger: 'onSubmit', whitespace: true },
            {
              max: 256,
              message: 'Maximum 256 characters',
              validateTrigger: 'onSubmit',
            },
          ]}
        >
          <Input placeholder="Company's address" style={{ ...buttonStyle }} size='large' />
        </Form.Item>
        <Form.Item
          name='userId'
          rules={[
            onlyLatinCharsDigitsSpecialSymbolsRule,
            {
              required: true,
              message: 'Please enter your SMART UID',
              validateTrigger: 'onSubmit',
            },
            {
              min: 3,
              message: translate('pages.register.errors.minUserId', 'Minimum 3 characters'),
              validateTrigger: 'onSubmit',
            },
            {
              max: 30,
              message: translate('pages.register.errors.maxUserId', 'Maximum 30 characters'),
              validateTrigger: 'onSubmit',
            },
            userIdContainsSpaceValidator,
            userIdAvailableValidator,
          ]}
        >
          <Input
            size='large'
            placeholder={translate('pages.register.fields.userId', 'SMART UID')}
            style={{ ...buttonStyle }}
          />
        </Form.Item>
        <Form.Item name='website' rules={websiteRules}>
          <Input
            size='large'
            placeholder={translate('pages.register.fields.website', 'Website')}
            onBlur={trimFormFieldValue('website', form)}
            style={{ ...buttonStyle }}
          />
        </Form.Item>
        <Col style={{ margin: '20px 0' }}>
          <Typography style={{ opacity: 0.4 }}>
            <Typography.Text style={{ fontWeight: 900 }}>Note:</Typography.Text> Your company details will be checked
            against
          </Typography>
          <Typography style={{ opacity: 0.4 }}>sanctions lists. Please ensure that all company information</Typography>
          <Typography style={{ opacity: 0.4 }}>provided above is correct before submitting this form.</Typography>
        </Col>
        <Row>
          <Col span={10} offset={7}>
            <Form.Item
              style={{
                marginBottom: 0,
              }}
            >
              <Button
                type='primary'
                size='large'
                htmlType='submit'
                loading={isLoading}
                block
                style={{ ...buttonStyle }}
              >
                {translate('pages.register.buttons.submit', 'Register')}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );

  return (
    <Layout style={layoutStyles} {...(wrapperProps ?? {})}>
      <Row justify='center' align='middle'>
        <Col xs={22}>
          {renderContent ? (
            renderContent(CardContent, PageTitle)
          ) : (
            <>
              {PageTitle}
              {CardContent}
            </>
          )}
        </Col>
      </Row>
    </Layout>
  );
};
